// @flow
import * as React from "react"
import { hot } from "react-hot-loader"
import { Layout } from "../../components/Layout"
import { Container } from "../../components/StyledComponents"
import { tRouteProps, tSiteProps } from "../../types"
import { css } from "emotion/macro"
import BigCalendar from "react-big-calendar"
import "react-big-calendar/lib/css/react-big-calendar.css"
import type { tCrmEvent } from "../../types"
import { EventList } from "../../components/EventList"
import moment from "moment-timezone"
import { color } from "../../styles/variables"
import { Select } from "../../components/Select"
import queryString from "query-string"
import debounce from "lodash.debounce"
import times from "lodash.times"
import isEqual from "lodash.isequal"
import { eventsCalendar } from "./style"
import { filterEventsByAll, localizer } from "./utils"
import { PageMeta } from "../../components/PageMeta"
import { ListIcon } from "../../components/Icons/ListIcon"
import { CalendarIcon } from "../../components/Icons/CalendarIcon"
import { RefreshIcon } from "../../components/Icons/RefreshIcon"
import { QuestionMarkIcon } from "../../components/Icons/QuestionMarkIcon"
import { BuildingIcon } from "../../components/Icons/BuildingIcon"
import { SearchIcon } from "../../components/Icons/SearchIcon"

const MONTH_FILTER_FORMAT = "MMMM YYYY"

const setFiltersOnURL = ({ month, search, topic, venue }) =>
  window.history.pushState(
    "events",
    "Events",
    `/events?month=${month ||
    ""}&search=${search}&topic=${topic}&venue=${venue}`
  )

const getTodayMonthFormatted = () => moment().format(MONTH_FILTER_FORMAT)

type tEventsCalendarContainer = {
  neon: {
    events: {
      events: Array<tCrmEvent>,
      venues: Array<string>,
      topics: Array<string>
    }
  },
  location: {
    search: string
  }
}

type tEventsCalendarContainerState = {
  calendarIsActive: boolean,
  events: Array<tCrmEvent>,
  filters: {
    month: string,
    search: string,
    topic: string,
    venue: string
  },
  currentView: string
}

class EventsCalendarContainer extends React.PureComponent<
  tEventsCalendarContainer & tRouteProps & tSiteProps,
  tEventsCalendarContainerState
> {
  viewOptions = ["List", "Calendar"]
  monthOptions = []
  initialState = {
    calendarIsActive: false,
    events: [],
    filters: {
      month: "",
      search: "",
      topic: "",
      venue: ""
    },
    currentView: this.viewOptions[0]
  }
  constructor(props: tEventsCalendarContainer) {
    super(props)

    const months = props.neon.events.events
      .map((e) => moment(e.startDate))
      .sort((a, b) => a.valueOf() - b.valueOf())
      .map((e) => e.format(MONTH_FILTER_FORMAT))
      .reduce(
        (months, month) =>
          months.includes(month) ? months : [...months, month],
        []
      )

    this.monthOptions = ["All", ...months]
    this.state = this.initialState
  }
  componentDidMount() {
    const params = queryString.parse(this.props.location.search)
    if (
      params &&
      (params.topic || params.venue || params.search || params.month)
    ) {
      this.setState({
        filters: {
          topic: params.topic ? params.topic : "",
          venue: params.venue ? params.venue : "",
          search: params.search ? params.search : "",
          month: params.month ? params.month : getTodayMonthFormatted()
        }
      })
    } else {
      this.setState({
        filters: {
          ...this.state.filters,
          month: getTodayMonthFormatted()
        }
      })
    }
  }
  componentDidUpdate(prevProps, prevState) {
    // Reset filters
    if (isEqual(this.state.filters, this.initialState.filters)) {
      this.setState({
        filters: { ...this.state.filters, month: getTodayMonthFormatted() }
      })
      setFiltersOnURL(this.initialState.filters)
    } else if (
      !isEqual(prevState.filters, this.state.filters) ||
      this.state.currentView !== prevState.currentView
    ) {
      // The Big Calendar component doesn't give us the option to set the month outside of the component
      const filterOverrides =
        this.state.currentView === this.viewOptions[1]
          ? { ...this.state.filters, month: "All" }
          : {}
      this.setState({
        ...this.state,
        filters: { ...this.state.filters, ...filterOverrides }
      })
      // We want to debounce so that we're not filtering on every character input in search box
      prevState.filters.search !== this.state.filters.search
        ? this.handleFilterBySearch(filterOverrides)
        : this.handleFilterEventsByAll(filterOverrides)
    }
  }
  handleFilterEventsByAll = (overrides = {}) => {
    this.setState({
      events: filterEventsByAll(
        this.props.neon.events.events,
        this.state.filters,
        ...overrides
      )
    })
    setFiltersOnURL(this.state.filters)
  }
  handleFilterBySearch = debounce(
    (overrides) => this.handleFilterEventsByAll(overrides),
    1000,
    {
      leading: false,
      trailing: true
    }
  )
  render() {
    const disableMonthSelect = this.state.currentView === this.viewOptions[1]
    return (
      <Container className={eventsCalendar}>
        <PageMeta
          title="Events Calendar"
          description="Center For Spiritual Living Events"
          image={this.props.brand.logo}
        />
        <h1>Events</h1>
        <div className="filters">
          <div className="month-wrapper">
            <Label>Month</Label>
            <Select
              icon={<CalendarIcon />}
              items={[
                ...this.monthOptions.filter((i) => i !== "All"),
                "All"
              ].reverse()}
              activeItem={disableMonthSelect ? "All" : this.state.filters.month}
              disabled={disableMonthSelect}
              onChange={(month) =>
                this.setState((st) => ({
                  filters: { ...st.filters, month }
                }))
              }
            />
          </div>
          <div className="topic-wrapper">
            <Label>Topic</Label>
            <Select
              icon={<QuestionMarkIcon />}
              items={this.props.neon.events.topics}
              placeholder="Select a topic"
              activeItem={this.state.filters.topic}
              onChange={(topic) =>
                this.setState((st) => ({
                  filters: {
                    ...st.filters,
                    topic: st.filters.topic
                      ? `${st.filters.topic},${topic}`
                      : topic
                  }
                }))
              }
            />
          </div>
          <div className="venue-wrapper">
            <Label>Venue</Label>
            <Select
              icon={<BuildingIcon />}
              activeItem={this.state.filters.venue}
              items={this.props.neon.events.venues}
              placeholder="Select a venue"
              onChange={(venue) =>
                this.setState((st) => ({
                  filters: {
                    ...st.filters,
                    venue: st.filters.venue
                      ? `${st.filters.venue},${venue}`
                      : venue
                  }
                }))
              }
            />
          </div>
          <div className="search-wrapper">
            <Label>Search</Label>
            <span className="search-icon">
              <SearchIcon />
            </span>
            <input
              type="text"
              value={this.state.filters.search}
              onChange={(event) => {
                event.persist()
                this.setState((st) => ({
                  filters: { ...st.filters, search: event.target.value }
                }))
              }}
            />
          </div>
          <div className="reset-wrapper">
            <button
              onClick={() =>
                this.setState({ filters: this.initialState.filters })
              }
            >
              <RefreshIcon /> Reset Filters
            </button>
          </div>
        </div>

        <ButtonBar
          className={css`
            button {
              display: inline-flex;
              align-items: center;

              &.is-active {
                svg path {
                  fill: ${color.rgb.orange[0]};
                }
              }
            }

            svg {
              width: 14px;
              height: 16px;

              path {
                fill: #d8d8d8;
              }
            }
          `}
        >
          <ButtonGroup
            label="View As"
            className={css`
              button {
                padding: 3px 6px;
              }
            `}
          >
            <button
              type="button"
              onClick={() =>
                this.setState({ currentView: this.viewOptions[0] })
              }
              className={
                this.state.currentView === this.viewOptions[0]
                  ? "is-active"
                  : ""
              }
            >
              <ListIcon />
            </button>
            <button
              type="button"
              onClick={() =>
                this.setState({ currentView: this.viewOptions[1] })
              }
              className={
                this.state.currentView === this.viewOptions[1]
                  ? "is-active"
                  : ""
              }
            >
              <CalendarIcon />
            </button>
          </ButtonGroup>
          {this.state.currentView === "List" && (
            <ButtonGroup label="Month">
              <button
                type="button"
                disabled={this.state.filters.month === "All"}
                onClick={() =>
                  this.setState((st) => ({
                    filters: {
                      ...st.filters,
                      month: "All"
                    }
                  }))
                }
              >
                All
              </button>
              <button
                type="button"
                disabled={getTodayMonthFormatted() === this.state.filters.month}
                onClick={() =>
                  this.setState((st) => ({
                    filters: {
                      ...st.filters,
                      month: getTodayMonthFormatted()
                    }
                  }))
                }
              >
                Today
              </button>
              <button
                type="button"
                disabled={
                  // 'All' is the first index, don't include it
                  this.monthOptions.indexOf(this.state.filters.month) <= 1
                }
                onClick={() => {
                  const currentIndex = this.monthOptions.indexOf(
                    this.state.filters.month
                  )
                  this.setState((st) => ({
                    filters: {
                      ...st.filters,
                      month: this.monthOptions[
                        // 'All' is the first index, don't include it
                        currentIndex > 1 ? currentIndex - 1 : 0
                      ]
                    }
                  }))
                }}
              >
                Back
              </button>
              <button
                disabled={
                  this.monthOptions.indexOf(this.state.filters.month) ===
                  this.monthOptions.length - 1
                }
                onClick={() => {
                  const currentIndex = this.monthOptions.indexOf(
                    this.state.filters.month
                  )
                  this.setState((st) => ({
                    filters: {
                      ...st.filters,
                      month: this.monthOptions[
                        currentIndex < this.monthOptions.length - 1
                          ? currentIndex + 1
                          : 0
                      ]
                    }
                  }))
                }}
                type="button"
              >
                Next
              </button>
            </ButtonGroup>
          )}
        </ButtonBar>

        {!this.state.events ||
          (this.state.events.length === 0 && (
            <Container>
              <div className="notice">No events match the current filters.</div>
            </Container>
          ))}

        {this.state.currentView === "List" ? (
          <EventList items={this.state.events} />
        ) : (
          <EventsCalendar
            history={this.props.history}
            items={this.state.events.map((item) => {
              item.start = new Date(item.start)
              item.end = new Date(item.end)
              item.title = item.name
              return item
            })}
          />
        )}
      </Container>
    )
  }
}

class EventsCalendar extends React.Component<{
  items: Array<tCrmEvent>,
  history: {
    push: (route: string) => void
  },
  style?: {}
}> {
  render() {
    /** This is a hack to troubleshoot a timezone offset issue that was causing some events to move over by one day
     * Took inspiration from: https://github.com/jquense/react-big-calendar/issues/809#issuecomment-675328667
     * Breadcrumbs: #date-offset-bug
     */
    const localOffset = moment().utcOffset()
    const offsetHours = (0 + (-localOffset)) / 60
    return (
      <div
        style={this.props.style ? this.props.style : {}}
        className={css`
          .rbc-event {
            background-color: ${color.rgb.purple[1]};
            border: 1px solid ${color.rgb.purple[1]};
          }

          .rbc-today {
            background-color: ${color.rgba.purple(0.15)[1]};
          }
        `}
      >
        <BigCalendar
          views={["month", "week", "day"]}
          /** We had an issue with some dates being offset from the desired day/time when rendered on the calendar.
           * If this problem persists consider using these accessor props to transform the dates before they are rendered
           * Breadcrumbs: #date-offset-bug
           */
          // startAccessor={(event) => moment(event.start).add(offsetHours, 'hours').toDate()}
          // endAccessor={(event) => moment(event.end).add(offsetHours, 'hours').toDate()}
          events={(() => {

            let reduced = this.props.items.reduce((acc, item) => {
              if (
                // Neon doesn't allow you to set a recurring weekly event, so CSL sets an event with a start date on first occurance and end date at the last occurance... In the cal view we make it obvious that it's just a recurring event
                item.categoryName === "Recurring event only occurs once weekly"
              ) {
                const occurCount = moment(item.endDate).diff(
                  moment(item.startDate),
                  "weeks"
                )

                const occurances = times(occurCount, (i) => {
                  const newStartDate = moment(item.startDate).add(i, "week")
                  return {
                    ...item,
                    startDate: newStartDate.format("YYYYMMDD"),
                    endDate: newStartDate.format("YYYYMMDD"),
                  }
                })

                return [...occurances, ...acc]
              } else[...acc, item]
              return [...acc, item]
            }, [])


            return reduced.map(item => {
              let startDate = moment(item.startDate)
              let endDate = moment(item.endDate)
              return {
                ...item,
                start: moment(
                  startDate.format(`ddd MMM DD YYYY ${item.startTime || "00:00:00"}`),
                  "ddd MMM DD YYYY HH:mm:ss ZZ"
                ).toDate(),
                end: moment(
                  endDate.format(`ddd MMM DD YYYY ${item.endTime || "00:00:00"}`),
                  "ddd MMM DD YYYY HH:mm:ss ZZ"
                ).toDate()
              }
            })
          })()}
          localizer={localizer}
          onSelectEvent={(event) =>
            (window.location = `https://secure.spiritualliving.org/np/clients/spiritualliving/event.jsp?event=${event.id}`)
          }
          style={{
            height: 600
          }}
        />
      </div>
    )
  }
}

function ButtonBar(props: { children: Node, className?: string }) {
  return (
    <div
      className={css`
        .rbc-toolbar {
          justify-content: space-between;
          margin-bottom: 40px;
        }

        button {
          &:disabled {
            cursor: not-allowed;
            opacity: 0.5;

            &:hover {
              background: transparent;
            }
          }
        }

        ${props.className ? props.className : ""}
      `}
    >
      <div className="rbc-toolbar">{props.children}</div>
    </div>
  )
}

function ButtonGroup(props: {
  children: Node,
  label: Node,
  className?: string
}) {
  return (
    <span className={`rbc-btn-group ${props.className ? props.className : ""}`}>
      <Label>{props.label}</Label>
      <div>{props.children}</div>
    </span>
  )
}

function Label(props: { children: Node }) {
  return (
    <div
      className={css`
        margin-bottom: 12px;
        line-height: 12px;
        letter-spacing: -0.24px;
        text-transform: uppercase;
        font-size: 12px;
        font-weight: bold;
        color: #333;
      `}
    >
      {props.children}
    </div>
  )
}

function RenderEventsCalendarContainer(props: tEventsCalendarContainer) {
  return (
    <Layout>
      {(routeProps) => <EventsCalendarContainer {...props} {...routeProps} />}
    </Layout>
  )
}

export default hot(module)(RenderEventsCalendarContainer)



// WEBPACK FOOTER //
// /opt/build/repo/src/screens/EventsCalendar/index.js